/**
 * Cross-render auth error channel.
 *
 * `handleRedirectPromise` runs in main.tsx BEFORE React mounts, but its
 * failure mode is precisely what the login screen needs to display. This
 * module holds a one-shot error captured at module scope; App reads (and
 * clears) it on mount and surfaces a banner.
 */
import type { AuthError } from '@azure/msal-browser'

export type AuthErrorInfo = {
  errorCode: string | null
  aadstsCode: string | null
  source: 'handleRedirectPromise' | 'loginRedirect' | 'googleLogin'
}

let pending: AuthErrorInfo | null = null

export const captureAuthError = (
  exception: unknown,
  source: AuthErrorInfo['source'],
): void => {
  const authErr = exception as Partial<AuthError>
  const message = authErr?.errorMessage
  const aadstsMatch = message?.match(/\bAADSTS(\d+)/)
  pending = {
    errorCode: authErr?.errorCode ?? null,
    aadstsCode: aadstsMatch ? `AADSTS${aadstsMatch[1]}` : null,
    source,
  }
}

export const consumePendingAuthError = (): AuthErrorInfo | null => {
  const value = pending
  pending = null
  return value
}

/**
 * Map an MSAL/AAD code to user-facing copy. Falls back to a generic message
 * so a future MSAL change can never strand the user without guidance.
 */
export const friendlyAuthMessage = (info: AuthErrorInfo): string => {
  if (info.aadstsCode === 'AADSTS50058' || info.errorCode === 'login_required') {
    return 'Sign-in session expired. Please try again.'
  }
  if (info.errorCode === 'interaction_in_progress') {
    return 'Another sign-in is in progress. Please wait a moment and try again.'
  }
  if (info.errorCode === 'state_mismatch' || info.errorCode === 'nonce_mismatch') {
    return "Sign-in couldn't complete safely. Please try again."
  }
  return "Sign-in didn't complete. Please try again — email support@aclera-ai.com if this keeps happening."
}
